import SearchComponent from '@components/inputs/search/Search.component';
import NewTableComponent from '@components/table/NewTable.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import {Box, Stack, Typography} from '@mui/material';
import {GridValueGetterParams} from '@mui/x-data-grid';
import {useEffectFn} from '@ngneat/effects-hooks';
import {emitOnce} from '@ngneat/elf';
import {useObservable} from '@ngneat/react-rxjs';
import {historiesQuery, History, historyService, HistoryType, searchHistoriesEffect} from '@store/settings/history';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import {SettingsRoutes} from '@utils/routes.utils';

const HistoryTab = () => {
  const navigate = useNavigate();
  const {t} = useTranslation();

  const searchHistories = useEffectFn(searchHistoriesEffect);

  const [{histories, loading}] = useObservable(historiesQuery.histories$);

  const [filters] = useObservable(historiesQuery.filters$);
  const [sortModel] = useObservable(historiesQuery.sort$);
  const [tablePagination] = useObservable(historiesQuery.pagination$);

  useEffect(() => {
    searchHistories({filters, tablePagination, sort: sortModel});
  }, [filters, searchHistories, sortModel, tablePagination]);

  return (
    <>
      <Stack flex={1} width="100%" spacing={2} p="25px">
        <Typography fontSize="30px" fontWeight="700">
          {t('routes.history')}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>
              <DateRangePickerComponent
                inputWidth={140}
                color="secondary"
                placeholderStart={t('invoices.executions.filters.from')}
                placeholderEnd={t('invoices.executions.filters.to')}
                startDate={filters.fromDate}
                endDate={filters.toDate}
                handleChangeStartDate={historyService.setFilters('fromDate')}
                handleChangeEndDate={historyService.setFilters('toDate')}
              />
            </Box>
            <Box width="500px">
              <SearchComponent
                value={filters.search}
                handleChange={(value) => {
                  emitOnce(() => {
                    historyService.setFilters('search')(value);
                    historyService.setPagination('page')(1);
                  });
                }}
              />
            </Box>
          </Stack>
          <Stack alignSelf="end">
            <TableFiltersComponent
              filters={filters}
              handleSave={historyService.setFilters('tableFilters')}
              handleReset={() => historyService.resetFilters()}
            />
          </Stack>
        </Stack>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT({
              field: 'createdAt',
              label: t('settings.histories.table.createdAt'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, History>) => params.row.createdAt,
              valueFormatter: (params) => (params.value ? t('global.fullDateTime', {date: new Date(params.value)}) : '-'),
              width: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'user',
              label: t('settings.histories.table.user'),
              fullWidth: true,
              valueGetter: (params: GridValueGetterParams<any, History>) => {
                if (!params.row.authorLastName || !params.row.authorFirstName) return ' - ';

                return `${params.row.authorLastName} ${params.row.authorFirstName}`;
              },
              minWidth: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'tableName',
              label: t('settings.histories.table.tableName'),
              fullWidth: true,
              minWidth: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'eventType',
              label: t('settings.histories.table.type'),
              valueFormatter: (params) => HistoryType.selectItems().find((s) => s.value === params.value)?.label || '',
              fullWidth: true,
              minWidth: 120,
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'id',
              label: t('settings.histories.table.id'),
              valueGetter: (params: GridValueGetterParams<any, History>) => `${params.row.ressourceId}`,
              fullWidth: true,
              width: 100,
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (history: History) => navigate(`../${SettingsRoutes.HISTORY}/${history.id}`),
            }),
          ]}
          handlePageSizeChange={(value) => {
            historyService.setPagination('limit')(value);
            historyService.setPagination('page')(1);
          }}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => historyService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={historyService.setSort}
          sortModel={[sortModel]}
          disableRowSelection
          totalRows={historiesQuery.totalRows}
          data={histories}
        />
      </Stack>
      <Outlet/>
    </>
  );
};

export default HistoryTab;
